import { useCallback } from 'react';

import {
  Currency,
  SupportedCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { toWei } from '@sorare/core/src/lib/wei';

type ReturnedProps = {
  formatPrice: (value: number, maxReachesEnd?: boolean) => string;
  sliderToPrice: (value: number) => string;
  priceToSlider: (value: number) => number;
};

export const useFormatFloorPricesFilters = (): ReturnedProps => {
  const { currency, fiatCurrency } = useCurrentUserCurrencies();
  const {
    toMonetaryAmount,
    toMonetaryAmountInFiatCurrency,
    getUserFiatAmountInMajorUnit,
  } = useMonetaryAmount();
  const { formatEth, formatNumber } = useIntlContext();

  const sliderToPrice = useCallback(
    (value: number): string => {
      const monetaryAmount = toMonetaryAmount({
        referenceCurrency: SupportedCurrency.EUR,
        eurCents: value,
      });

      if (currency === Currency.FIAT) {
        return getUserFiatAmountInMajorUnit(monetaryAmount).toFixed(2);
      }
      return monetaryAmount.eth.toString();
    },
    [currency, toMonetaryAmount, getUserFiatAmountInMajorUnit]
  );

  const formatPrice = useCallback(
    (value: number, maxReachesEnd?: boolean) => {
      const price = sliderToPrice(value);

      let formattedNumber;
      if (currency === Currency.FIAT) {
        formattedNumber = formatNumber(Number(price), {
          style: 'currency',
          currency: fiatCurrency,
        });
      } else {
        formattedNumber = formatEth(price);
      }

      return `${formattedNumber}${maxReachesEnd ? '+' : ''}`;
    },
    [currency, fiatCurrency, formatEth, formatNumber, sliderToPrice]
  );

  const priceToSlider = useCallback(
    (value: number): number => {
      if (currency === Currency.FIAT) {
        return toMonetaryAmountInFiatCurrency(value * 100).eurCents;
      }
      return toMonetaryAmount({
        wei: toWei(value),
        referenceCurrency: SupportedCurrency.WEI,
      }).eurCents;
    },
    [currency, toMonetaryAmountInFiatCurrency, toMonetaryAmount]
  );

  return {
    formatPrice,
    sliderToPrice,
    priceToSlider,
  };
};
